import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  MapPin, Phone, Mail, Clock, CalendarIcon, MessageCircle,
  CheckCircle2, ArrowUpRight, User, AtSign, Phone as PhoneIcon,
  Navigation, Star,
} from "lucide-react";
import { CLINIC, REASON_OPTIONS, FAQS } from "@/data/site";
import { Reveal } from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM",
  "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM",
];

const EMPTY = { name: "", email: "", phone: "", reason: "", time: "", notes: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const waLink = (() => {
    const parts = [
      "Hello, I'd like to book an appointment with Dr. Pallavi Mishra.",
      form.name && `Name: ${form.name}`,
      form.phone && `Phone: ${form.phone}`,
      date && `Preferred date: ${format(date, "PPP")}`,
      form.time && `Preferred time: ${form.time}`,
      form.reason && `Reason: ${form.reason}`,
      form.notes && `Notes: ${form.notes}`,
    ].filter(Boolean);
    return `https://wa.me/${CLINIC.phoneRaw}?text=${encodeURIComponent(parts.join("\n"))}`;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitted(true);
    toast.success("Appointment request received!", {
      description: "We'll confirm your slot shortly. You can also send it via WhatsApp for a faster response.",
    });
  };

  const resetForm = () => { setForm(EMPTY); setDate(null); setSubmitted(false); };

  return (
    <>
      <PageHero
        crumb="Contact"
        eyebrow="Contact Us"
        titleLines={["Get in Touch —", "Book Your Visit", "in Varanasi"]}
        subtitle="Schedule your appointment or send us your queries. We're easily accessible from Kabir Nagar, Durgakund and surrounding areas."
      />

      <section className="pb-24 md:pb-32 bg-paper" data-testid="contact-body">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-[2rem] border border-line bg-surface p-7 md:p-10">
                {!submitted ? (
                  <>
                    <h2 className="font-display text-3xl md:text-4xl text-ink font-light mb-2">Appointment Request</h2>
                    <p className="text-sage mb-8">Fill in your details and we&rsquo;ll confirm your consultation slot.</p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Field label="Full Name" icon={User} required>
                          <Input
                            data-testid="form-name"
                            value={form.name}
                            onChange={(e) => set("name")(e.target.value)}
                            placeholder="Your name"
                            className="h-12 bg-paper border-line focus-visible:ring-brand"
                          />
                        </Field>
                        <Field label="Phone" icon={PhoneIcon} required>
                          <Input
                            data-testid="form-phone"
                            value={form.phone}
                            onChange={(e) => set("phone")(e.target.value)}
                            placeholder="+91 ..."
                            className="h-12 bg-paper border-line focus-visible:ring-brand"
                          />
                        </Field>
                      </div>

                      <Field label="Email" icon={AtSign}>
                        <Input
                          data-testid="form-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => set("email")(e.target.value)}
                          placeholder="you@example.com"
                          className="h-12 bg-paper border-line focus-visible:ring-brand"
                        />
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Field label="Preferred Date" icon={CalendarIcon}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                data-testid="form-date-trigger"
                                className="flex h-12 w-full items-center justify-between rounded-md border border-line bg-paper px-3 text-sm text-left focus:outline-none focus:ring-1 focus:ring-brand"
                              >
                                <span className={date ? "text-ink" : "text-muted-foreground"}>
                                  {date ? format(date, "PPP") : "Select a date"}
                                </span>
                                <CalendarIcon size={16} className="text-sage" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={date}
                                onSelect={setDate}
                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>

                        <Field label="Preferred Time" icon={Clock}>
                          <Select value={form.time} onValueChange={set("time")}>
                            <SelectTrigger data-testid="form-time" className="h-12 bg-paper border-line focus:ring-brand">
                              <SelectValue placeholder="Select a time" />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_SLOTS.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>

                      <Field label="Reason for Visit">
                        <Select value={form.reason} onValueChange={set("reason")}>
                          <SelectTrigger data-testid="form-reason" className="h-12 bg-paper border-line focus:ring-brand">
                            <SelectValue placeholder="Select a reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {REASON_OPTIONS.map((r) => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>

                      <Field label="Additional Notes">
                        <Textarea
                          data-testid="form-notes"
                          value={form.notes}
                          onChange={(e) => set("notes")(e.target.value)}
                          placeholder="Anything you'd like us to know..."
                          rows={4}
                          className="bg-paper border-line focus-visible:ring-brand resize-none"
                        />
                      </Field>

                      <button
                        type="submit"
                        data-testid="form-submit"
                        className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand hover:bg-brand-dark text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-colors duration-300"
                      >
                        Request Appointment <ArrowUpRight size={18} />
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="py-10 text-center" data-testid="form-success">
                    <span className="grid place-items-center w-16 h-16 rounded-full bg-mint text-brand mx-auto mb-6">
                      <CheckCircle2 size={32} />
                    </span>
                    <h2 className="font-display text-3xl md:text-4xl text-ink font-light mb-3">Request Received</h2>
                    <p className="text-sage max-w-md mx-auto mb-8">
                      Thank you{form.name ? `, ${form.name}` : ""}. We&rsquo;ve noted your request and will confirm your slot shortly. For the fastest response, send it directly via WhatsApp.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="success-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-dark text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] transition-colors"
                      >
                        <MessageCircle size={17} /> Send via WhatsApp
                      </a>
                      <button
                        onClick={resetForm}
                        data-testid="success-reset"
                        className="inline-flex items-center gap-2 rounded-full border border-line hover:border-brand text-ink px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] transition-colors"
                      >
                        New Request
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          {/* Sticky NAP + map */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal>
                <div className="rounded-[2rem] border border-line bg-brand-deep text-white p-8 grain relative overflow-hidden">
                  <div className="relative space-y-6">
                    <h3 className="font-display text-2xl">Clinic Information</h3>
                    <InfoRow icon={MapPin} label="Address" value={CLINIC.address} />
                    <InfoRow icon={Phone} label="Phone" value={CLINIC.phone} href={`tel:+${CLINIC.phoneRaw}`} />
                    <InfoRow icon={Mail} label="Email" value={CLINIC.email} href={`mailto:${CLINIC.email}`} />
                    <div className="flex gap-4">
                      <Clock size={18} className="text-brand shrink-0 mt-0.5" />
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-brand font-semibold mb-1">Clinic Hours</div>
                        {CLINIC.hours.map((h) => (
                          <div key={h.day} className="text-sm text-mint/85">{h.day}: <span className="text-mint/60">{h.time}</span></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="rounded-[2rem] border border-line overflow-hidden bg-surface">
                  <iframe
                    title="Dr. Pallavi Mishra Clinic Location"
                    src={CLINIC.mapEmbed}
                    className="w-full h-72 map-tint"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="contact-map"
                  />
                  <div className="p-5">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <a
                        href={CLINIC.directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="directions-btn"
                        className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand hover:bg-brand-dark text-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300"
                      >
                        <Navigation size={15} /> Directions
                      </a>
                      <a
                        href={CLINIC.reviewsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="reviews-btn"
                        className="group inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand hover:bg-brand hover:text-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-300"
                      >
                        <Star size={15} /> Reviews
                      </a>
                    </div>
                    <p className="text-sm text-sage">
                      Easily accessible from Kabir Nagar, Durgakund and surrounding areas of Varanasi.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <FAQSection items={FAQS.contact} heading="Booking & Visit — FAQs" />
    </>
  );
}

function Field({ label, icon: FieldIcon, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold text-sage">
        {FieldIcon && <FieldIcon size={13} className="text-brand" />}
        {label}{required && <span className="text-brand">*</span>}
      </Label>
      {children}
    </div>
  );
}

function InfoRow({ icon: RowIcon, label, value, href }) {
  const content = (
    <div className="flex gap-4">
      <RowIcon size={18} className="text-brand shrink-0 mt-0.5" />
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand font-semibold mb-1">{label}</div>
        <div className="text-sm text-mint/85 leading-relaxed">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a> : content;
}
